"use client";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../slice/hooks";
import StoreBox from "./store-box";
import CartModal from "./cart-modal";
import Progress from "./progress";
import { getActiveUser } from "../slice/auth-slice";
import {
  getLoggedinUser,
  useFetchGuestCollections,
  useFetchUserCollections,
} from "../../lib/util";
import { useToken } from "../../lib/context/auth-context";

import Skeleton from "react-loading-skeleton";
import { useSwitchingOrgs } from "../organisation/useSwitchingOrgs";
import { cartIcon } from "../../assets";

import { useHistoryManager } from "../history/useHistoryManager";
import {
  getAccessPoint,
  getAccessPoints,
  getOrganisation,
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";
import { setActiveCategory, setCategories } from "../slice/category-slice";
import { useQueryClient } from "@tanstack/react-query";

const MarketplaceSkeleton = () => {
  return (
    <div className="cart-container">
      <div className="cart-div">
        {Array.from({ length: 3 }).map((_, storeIdx) => (
          <div
            key={storeIdx}
            style={{
              padding: "10px",
              paddingTop: 0,
              marginBottom: "24px",
              borderRadius: "12px",
              background: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <Skeleton width={48} height={48} borderRadius={8} />
              <Skeleton width={180} height={22} />
            </div>

            <div
              style={{
                borderRadius: "12px",
                padding: "16px",
                background: "#fafafa",
                display: "flex",
                flexDirection: "column",
                gap: "2rem",
              }}
            >
              {Array.from({ length: 3 }).map((_, itemIdx) => (
                <div
                  key={itemIdx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Skeleton width={56} height={56} borderRadius={8} />

                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    <Skeleton width="40%" height={14} />
                    <Skeleton width="70%" height={16} />
                  </div>

                  <Skeleton width={40} height={28} borderRadius={6} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const HostHeader = ({ host }) => {
  const [logoLoaded, setLogoLoaded] = useState(false);

  const hostImage = host?.image;
  const hostName = host?.name;

  const logoSize = 48;

  return (
    <div
      style={{
        display: "flex",
        marginBottom: "2rem",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        aria-hidden
        style={{
          width: logoSize,
          height: logoSize,
          borderRadius: 8,
          overflow: "hidden",
          flexShrink: 0,
          background: "#f6f6f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {hostImage ? (
          <>
            {!logoLoaded && <Skeleton width={logoSize} height={logoSize} />}
            <img
              src={hostImage}
              alt={hostName ? `${hostName} logo` : "Organisation logo"}
              onLoad={() => setLogoLoaded(true)}
              onError={() => setLogoLoaded(true)}
              style={{
                width: logoSize,
                height: logoSize,
                objectFit: "cover",
                display: logoLoaded ? "block" : "none",
              }}
            />
          </>
        ) : null}
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        {hostName ? (
          <span
            style={{
              fontSize: "2.5rem",
              fontWeight: 500,
              lineHeight: 1.1,
            }}
          >
            {hostName}
          </span>
        ) : null}
      </div>
    </div>
  );
};

const MarketplaceCartPage = (props) => {
  const {
    closeModal,
    showProduct,
    activateBackButton,
    storePath,
    modal,
    showProgress,
    setShowProgress,
    navLocation,
    urlData,
  } = props;

  const dispatch = useAppDispatch();
  const [isPageMode, setIsPageMode] = useState(modal ? false : true);
  const [publicUser, setPublicUser] = useState(null);

  const { loggedInUser } = useAppSelector((state) =>
    getActiveUser(state, getLoggedinUser()),
  );
  const organisation = useAppSelector(getOrganisation);
  const accessPoint = useAppSelector(getAccessPoint);
  const accessPoints = useAppSelector(getAccessPoints);

  const [cartCollections, setCartCollections] = useState([]);
  const { token } = useToken();
  const userId = loggedInUser?.user?.userId ?? null;
  const loggedToken = loggedInUser?.token ?? null;
  const queryClient = useQueryClient();

  const [checkoutStates, setCheckoutStates] = useState({});
  const [activeProgressCollectionId, setActiveProgressCollectionId] =
    useState(null);

  const [isSwitching, setIsSwitching] = useState(false);

  const { data: userCollectionsRes, isFetching: isFetchingUserCollections } =
    useFetchUserCollections({
      userId,
      token: loggedToken,
      enabled: !!userId,
      cartSource: "MARKETPLACE",
    });

  const { data: guestCollectionsRes, isFetching: isFetchingGuestCollections } =
    useFetchGuestCollections({
      token,
      enabled: !userId,
      cartSource: "MARKETPLACE",
    });

  const isFetchingCollections =
    isFetchingUserCollections || isFetchingGuestCollections;

  useEffect(() => {
    const userCollections = userCollectionsRes?.data?.collections ?? null;
    const guestCollections = guestCollectionsRes?.data?.collections ?? null;
    const collections = userId ? userCollections : (guestCollections ?? []);

    setCartCollections(Array.isArray(collections) ? collections : []);
  }, [userCollectionsRes, guestCollectionsRes, userId]);

  const visibleCollections = useMemo(
    () =>
      (cartCollections || []).filter(
        (c) => (c?.paymentItem ?? []).length !== 0,
      ),
    [cartCollections],
  );

  const groupedByHost = useMemo(() => {
    const map = new Map();

    for (const collection of visibleCollections) {
      const host = collection?.host ?? {};
      const hostId = host.profileId ?? host.orgId ?? "unknown-host";

      if (!map.has(hostId)) {
        map.set(hostId, { host, hostId, collections: [] });
      }

      map.get(hostId).collections.push(collection);
    }

    return Array.from(map.values());
  }, [visibleCollections]);

  const { startSwitch, isSwitching: hookSwitching } = useSwitchingOrgs({
    token,
  });

  const { navigateTo } = useHistoryManager(storePath);

  const handleActivateBackButton = useCallback(
    (show, onClickBackButton = null) => {
      activateBackButton?.(show, onClickBackButton);
      setShowProgress(show);
    },
    [activateBackButton, setShowProgress],
  );

  const getCheckoutState = useCallback(
    (collectionId) => checkoutStates[collectionId] ?? "cartItems",
    [checkoutStates],
  );

  const setCheckoutStateFor = useCallback((collectionId, newState) => {
    setCheckoutStates((prev) => ({
      ...prev,
      [collectionId]: newState,
    }));
  }, []);

  useEffect(() => {
    setIsSwitching(hookSwitching);
  }, [hookSwitching]);

  const showSwitchingSkeleton =
    isSwitching || (isFetchingCollections && cartCollections.length === 0);

  return (
    <StoreBox {...props}>
      <div className="cart-container">
        <div className="cart-div">
          <div className="marketplaceCartHeading">
            <p>Checkout your goods from our stores.</p>
          </div>

          {showSwitchingSkeleton ? (
            <MarketplaceSkeleton />
          ) : groupedByHost.length > 0 ? (
            groupedByHost.map((group, groupIdx) => {
              const groupKey = group.hostId;

              return (
                <div key={groupKey} style={{ padding: "20px" }}>
                  {groupIdx !== 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        margin: "40px 0",
                        marginTop: "0",
                        color: "#777",
                        fontWeight: 500,
                        fontSize: "1.2rem",
                      }}
                    >
                      <div
                        style={{ flex: 1, borderTop: "2px solid #c3c3c3" }}
                      />
                      <span
                        style={{
                          padding: "0 16px",
                          whiteSpace: "nowrap",
                          fontSize: "1.6rem",
                          color: "#333",
                          fontWeight: 600,
                        }}
                      >
                        Store: {group.host?.name || "New Store"}
                      </span>
                      <div
                        style={{ flex: 1, borderTop: "2px solid #c3c3c3" }}
                      />
                    </div>
                  )}

                  <HostHeader host={group.host} />

                  {group.collections.map((collection) => {
                    const collectionId = collection?.collectionId;
                    const itemsLength = collection?.paymentItem?.length ?? 0;
                    const key = `${collectionId}-${itemsLength}`;

                    return (
                      <div key={key}>
                        <CartModal
                          cart={collection}
                          showProduct={showProduct}
                          closeModal={closeModal}
                          accessPoint={accessPoint}
                          accessPoints={accessPoints}
                          activateBackButton={handleActivateBackButton}
                          checkoutState={getCheckoutState(collectionId)}
                          setCheckoutState={(st) =>
                            setCheckoutStateFor(collectionId, st)
                          }
                          publicUser={publicUser}
                          setPublicUser={setPublicUser}
                          isPageMode={isPageMode}
                          storePath={storePath}
                          navLocation={navLocation}
                          userId={userId}
                          userToken={loggedToken}
                          token={token}
                          activeProgressCollectionId={
                            activeProgressCollectionId
                          }
                          setActiveProgressCollectionId={
                            setActiveProgressCollectionId
                          }
                          startSwitch={startSwitch}
                          organisation={organisation}
                          cartSource="MARKETPLACE"
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })
          ) : (
            <div className="empty-cart-message">
              <span>{cartIcon}</span>
              <h3>Your cart is empty.</h3>
              <p>Looks like you haven't added anything to your cart yet.</p>
              <button
                className="checkout-secondary-button"
                onClick={() => navigateTo("home", {}, false)}
              >
                Continue shopping
              </button>
            </div>
          )}
        </div>
      </div>
    </StoreBox>
  );
};

export default MarketplaceCartPage;
